import axios from 'axios';
import React from 'react';
import { Button, Card, notification, Spin, Badge, Empty, Space, Typography, Tag, Tooltip, Popconfirm, Tabs } from 'antd';
import { ReloadOutlined, CheckOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { request } from '@/request';
import history from '@/utils/history';
import { getColor, getBadges } from '@/hooks/useNotifications';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { useSelector } from 'react-redux';

const NotificationComponent = ({
    tabCounts,
    activeType,
    handleTabChange,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
    fetchCounts,
    deleteNotification,
    notifications = [],
    isNotificationLoading,
    loadMore,
    hasMore
}) => {

    // Style utilities are now imported from useNotifications hook

    const formatDate = (date) => {
        const now = new Date();
        const notifDate = new Date(date);
        const diffInMs = now - notifDate;
        const diffInMinutes = Math.floor(diffInMs / 60000);
        const diffInHours = Math.floor(diffInMs / 3600000);
        const diffInDays = Math.floor(diffInMs / 86400000);
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
        if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        return notifDate.toLocaleDateString();
    };
    const currentAdmin = useSelector(selectCurrentAdmin);
    const [deletingIds, setDeletingIds] = React.useState(new Set());
    const [markingReadIds, setMarkingReadIds] = React.useState(new Set());

    const handleMarkAsRead = async (e, id) => {
        e.stopPropagation();
        setMarkingReadIds(prev => new Set(prev).add(id));
        await markAsRead(id);
        setMarkingReadIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const handleDelete = async (notificationId) => {
        setDeletingIds(prev => new Set(prev).add(notificationId));

        await deleteNotification(notificationId);

        // Simulating completion since optimistic update handles immediate UI
        setDeletingIds(prev => {
            const next = new Set(prev);
            next.delete(notificationId);
            return next;
        });
    };

    const handleNotificationClick = (notif) => {
        // Mark as read if not already
        if (!notif.isRead) {
            markAsRead(notif._id);
        }

        // Navigate based on type
        switch (notif.notificationType) {
            case 'leave_request':
            case 'leave_approval':
                history.push('/LeaveRequestList');
                break;
            case 'overtime_request':
            case 'overtime_approval':
                history.push('/overtime-extrahours');
                break;
            case 'leave_benefit':
                // For HR/Admins managing requests
                history.push('/leavebenefitrequests');
                break;
            case 'leave_benefit_status':
                // For employees viewing their own benefits
                history.push('/leavesummary');
                break;
            case 'announcement':
            case 'system_announcement':
                history.push('/announcement-feeds');
                break;
            case 'appraisal':
                history.push('/appraisaldashboard');
                break;
            case 'complaint':
                history.push('/complaint-list');
                break;
            case 'suggestion':
                history.push('/suggestion-list');
                break;
            case 'salary_slip':
                if (currentAdmin.role === 'super_admin' || currentAdmin.role === 'hr_admin' || currentAdmin.role === 'finance_admin') {
                    history.push('/salary-management');
                } else {
                    history.push('/my-profile');
                }
                break;
            case 'leave_balance':
                history.push('/leaves');
                break;
                // case 'reminder':
                // Reminders are primarily on the dashboard/attendance report
                // We'll navigate there. Logic in AttendanceReport will handle opening if needed via query params later,
                // or just landing the beneficiary on the dashboard is a good start.
                // history.push('/');
                break;
            case 'profile_status':
            case 'designation':
                history.push('/my-profile');
                break;
            case 'alert':
                // AI alert notification — navigate to PM page and let it open the
                // correct alert panel or task drawer via location.state
                history.push({
                    pathname: '/project-management',
                    state: {
                        alertId: notif.relatedData?.alertId,
                        taskId: notif.relatedData?.taskId,
                    },
                });
                break;
            default:
                // No specific navigation
                break;
        }
    };

    const handleScroll = (e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 50) { // Load when near bottom
            if (!isNotificationLoading && hasMore) {
                loadMore();
            }
        }
    };

    const items = [
        { key: 'all', label: 'All', badge: tabCounts.all },
        { key: 'leave_request', label: 'Leave Request', badge: tabCounts.leave_request },
        { key: 'leave_approval', label: 'Leave Approval', badge: tabCounts.leave_approval },
        { key: 'leave_benefit', label: 'Leave Benefit', badge: tabCounts.leave_benefit },
        { key: 'overtime_request', label: 'Overtime Request', badge: tabCounts.overtime_request },
        { key: 'overtime_approval', label: 'Overtime Approval', badge: tabCounts.overtime_approval },
        { key: 'break_alert', label: 'Break Alert', badge: tabCounts.break_alert },
        { key: 'announcement', label: 'Announcement', badge: tabCounts.announcement },
        { key: 'others', label: 'Others', badge: tabCounts.others },
    ];

    return (
        <div className="notification-popover-content" style={{ width: '420px' }}>
            <Tabs
                activeKey={activeType}
                onChange={handleTabChange}
                size="small"
                centered
                tabBarStyle={{ marginBottom: '12px' }}
                items={items.map(item => ({
                    key: item.key,
                    label: (
                        <span>
                            {item.label}
                            {item.badge > 0 && (
                                <Badge
                                    count={item.badge}
                                    style={{ backgroundColor: '#1677FF', marginLeft: '6px', fontSize: '10px' }}
                                    size="small"
                                />
                            )}
                        </span>
                    ),
                }))}
            />

            <div
                style={{ maxHeight: '450px', overflowY: 'auto', padding: '0 8px' }}
                onScroll={handleScroll}
            >
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                    <Space>
                        {tabCounts.all > 0 && (
                            <Button
                                icon={<CheckCircleOutlined />}
                                onClick={markAllAsRead}
                                size="small"
                            >
                                Mark All Read
                            </Button>
                        )}
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                fetchNotifications(1, activeType, true);
                                fetchCounts();
                            }}
                            loading={isNotificationLoading}
                            size="small"
                            type="primary"
                        >
                            Refresh
                        </Button>
                    </Space>
                </div>

                {isNotificationLoading && notifications.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin tip="Loading..." />
                    </div>
                ) : notifications.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {notifications.map((notif) => {
                            const badges = getBadges(notif);
                            return (
                                <Card
                                    key={notif._id}
                                    size="small"
                                    hoverable
                                    onClick={() => handleNotificationClick(notif)}
                                    style={{
                                        borderLeft: `4px solid ${getColor(notif)}`,
                                        backgroundColor: document.documentElement.getAttribute('data-theme') === 'dark'
                                            ? (!notif.isRead ? '#0a2540' : '#1f1f1f')
                                            : (!notif.isRead ? '#f0faff' : '#fff'),
                                        cursor: 'pointer',
                                        marginBottom: '8px'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                <div style={{ fontWeight: 'bold', fontSize: '14px', color: document.documentElement.getAttribute('data-theme') === 'dark' ? 'rgba(255,255,255,0.95)' : 'inherit' }}>{notif.title}</div>
                                                {!notif.isRead && <Badge status="processing" color="#1890ff" />}
                                            </div>
                                            <div style={{ fontSize: '12px', color: document.documentElement.getAttribute('data-theme') === 'dark' ? 'rgba(255,255,255,0.65)' : '#444', lineHeight: '1.4' }}>{notif.message}</div>

                                            {badges.length > 0 && (
                                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                                                    {badges.map((badge, idx) => (
                                                        <span
                                                            key={idx}
                                                            style={{
                                                                display: 'inline-block',
                                                                padding: '1px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '10px',
                                                                fontWeight: 'bold',
                                                                backgroundColor: document.documentElement.getAttribute('data-theme') === 'dark'
                                                                    ? (badge.bgColor === '#f6ffed' ? '#162312' : badge.bgColor === '#fff1f0' ? '#2a1215' : badge.bgColor === '#fff7e6' ? '#2b2111' : badge.bgColor?.includes('#fff') ? '#2a2a2a' : badge.bgColor || badge.color)
                                                                    : (badge.bgColor || badge.color),
                                                                color: document.documentElement.getAttribute('data-theme') === 'dark'
                                                                    ? (badge.textColor === '#52c41a' ? '#95de64' : badge.textColor === '#f5222d' ? '#ff7875' : badge.textColor === '#fa8c16' ? '#ffa940' : badge.textColor || '#ffffff')
                                                                    : (badge.textColor || '#ffffff'),
                                                                border: badge.borderColor ? `1px solid ${document.documentElement.getAttribute('data-theme') === 'dark'
                                                                    ? (badge.borderColor === '#b7eb8f' ? '#274916' : badge.borderColor === '#ffa39e' ? '#4a1f1c' : badge.borderColor === '#ffd591' ? '#613400' : badge.borderColor?.includes('#ff') ? '#555' : badge.borderColor)
                                                                    : badge.borderColor}` : 'none'
                                                            }}
                                                        >
                                                            {badge.text}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            <div style={{ fontSize: '10px', color: document.documentElement.getAttribute('data-theme') === 'dark' ? 'rgba(255,255,255,0.45)' : '#999', marginTop: '6px' }}>
                                                {formatDate(notif.createdAt)}
                                            </div>
                                        </div>
                                        <Space direction="vertical" size="small" onClick={(e) => e.stopPropagation()}>
                                            {!notif.isRead && (
                                                <Tooltip title="Mark as read">
                                                    {markingReadIds.has(notif._id) ? (
                                                        <Spin size="small" />
                                                    ) : (
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<CheckOutlined />}
                                                            onClick={(e) => handleMarkAsRead(e, notif._id)}
                                                        />
                                                    )}
                                                </Tooltip>
                                            )}
                                            {deletingIds.has(notif._id) ? (
                                                <Spin size="small" />
                                            ) : (
                                                <Popconfirm
                                                    title="Delete this notification?"
                                                    onConfirm={(e) => handleDelete(notif._id)}
                                                    okText="Yes"
                                                    cancelText="No"
                                                    onCancel={(e) => e?.stopPropagation()}
                                                >
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </Popconfirm>
                                            )}
                                        </Space>
                                    </div>
                                </Card>
                            );
                        })}
                        {isNotificationLoading && (
                            <div style={{ textAlign: 'center', padding: '10px' }}>
                                <Spin size="small" />
                            </div>
                        )}
                    </div>
                ) : (
                    <Empty description="No notifications" />
                )}
            </div>
        </div>
    );
};

export default NotificationComponent;
